import { createSlice } from '@reduxjs/toolkit';
import { calendarApiV4 as api } from 'api';

import { transformFiltersToQuery } from '../utils/transformFilterToQuery';

export const availabilitySlice = createSlice({
    name: 'availabilityV4',
    initialState: {
        pending: false,
        savePending: false,
        availabilities: [],
        error: null,
        deletePending: false,
        deleteError: null,
        deleteId: null,
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        startDelete: (state, action) => {
            state.deletePending = true;
            state.deleteId = action.payload;
        },
        stopDelete: (state, action) => {
            state.deletePending = false;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        addAvailability: (state, action) => {
            state.savePending = false;
            state.availabilities = [action.payload, ...state.availabilities];
        },
        editAvailability: (state, action) => {
            state.savePending = false;
            const index = state.availabilities.findIndex((availability) => availability.id === action.payload.id);
            if (index !== -1) {
                state.availabilities = [
                    ...state.availabilities.slice(0, index),
                    { ...state.availabilities[index], ...action.payload },
                    ...state.availabilities.slice(index + 1),
                ];
            }
        },
        deleteAvailability: (state, action) => {
            state.deletePending = false;
            const index = state.availabilities.findIndex((availability) => availability.id === action.payload.id);
            if (index !== -1) {
                state.availabilities = [
                    ...state.availabilities.slice(0, index),
                    ...state.availabilities.slice(index + 1),
                ];
            }
        },
        setAvailabilities: (state, action) => {
            state.pending = false;
            state.availabilities = action.payload.items;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.callToActions = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
    },
});

export const {
    start,
    stop,
    setAvailabilities,
    addAvailability,
    editAvailability,
    deleteAvailability,
    stopDelete,
    setError,
    startDelete,
    startSave,
    stopSave,
    setSaveError,
} = availabilitySlice.actions;

export const getAvailabilities = (state) => state.availabilityV4.availabilities;
export const getAvailabilitiesPending = (state) => state.availabilityV4.pending;
export const getAvailabilitiesSavePending = (state) => state.availabilityV4.savePending;
export const getDeletePending = (state) => state.availabilityV4.deletePending;
export const getDeleteId = (state) => state.availabilityV4.deleteId;
export const getAvailabilitiesTotal = (state) => state.availabilityV4.total;
export const getAvailabilitiesCurrentPage = (state) => state.availabilityV4.currentPage;
export const getAvailabilitiesTotalPages = (state) => state.availabilityV4.totalPages;

export const fetchAvailabilitiesAction =
    ({ queryParams, onSuccess, onFailure, options } = {}) =>
    async (dispatch) => {
        try {
            const shouldUseState = options?.useState ?? true;
            let query = transformFiltersToQuery(queryParams);
            dispatch(start());
            api.get('availabilities' + query)
                .then((response) => {
                    if (shouldUseState) {
                        dispatch(setAvailabilities(response.data));
                    }
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error(e.message);
        }
    };

export const addAvailabilityAction =
    ({ dto, onSuccess, onFailure, options } = {}) =>
    async (dispatch) => {
        try {
            const shouldUseState = options?.useState ?? true;
            dispatch(startSave());
            api.post('availabilities', dto)
                .then((response) => {
                    if (shouldUseState) {
                        dispatch(addAvailability(response?.data));
                    }
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error(e.message);
        }
    };

export const editAvailabilityAction =
    ({ id, dto, onSuccess, onFailure, options } = {}) =>
    async (dispatch) => {
        try {
            const shouldUseState = options?.useState ?? true;
            dispatch(startSave());
            api.put(`availabilities/` + id, dto)
                .then((response) => {
                    if (shouldUseState) {
                        dispatch(editAvailability(response.data));
                    }
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error('error', e);
        }
    };

export const deleteAvailabilityAction = (id, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(startDelete(id));
        api.delete('availabilities/' + id)
            .then((response) => {
                dispatch(deleteAvailability({ id }));
                if (onSuccess) {
                    onSuccess();
                }
                return response;
            })
            .catch((error) => {
                dispatch(setError(error));
                if (onFailure) {
                    onFailure();
                }
            });
    } catch (e) {
        dispatch(stopDelete());
        return console.error('error', e);
    }
};

export const fetchAvailabilitiesByCalendarAction =
    (calendarId, { queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            dispatch(start());
            api.get(`calendars/${calendarId}/weekly-schedules`)
                .then((response) => {
                    dispatch(setAvailabilities(response.data));
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error(e.message);
        }
    };

export const fetchAvailabilitySlotsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(queryParams);
            // dispatch(start());
            api.get('availabilities/slots' + query)
                .then((response) => {
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            // dispatch(stop());
            return console.error(e.message);
        }
    };

export default availabilitySlice.reducer;
